/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Card, Grid, Notification } from '@scuf/common';
import React, { useEffect, useState, useContext } from 'react';
import {
  IAny,
  IBillBreakdownChartAggregations
} from '../../../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  IStackedColumnLineChartProps,
  StackedColumnLineChart,
  consumptionUsageTooltipFormatter,
  stackedColumnLineTooltipFormatter,
  electricityCostLineTooltipFormatter
} from '../../../../shared/UtilityBillBreakdown/StackedColumnLineChart/StackedColumnLineChart';
import chartColors from '@scuf/charts/dist/utils/chartColors';
import defaultColors from '@scuf/charts/dist/utils/defaultColors';
import { useTranslation } from '@scuf/localization';
import _ from 'lodash';
import { getMaxConvertedValueFromArray } from '../../../../../main';
import {
  formatCurrency,
  formatTooltipHeaderUtilityBreakdown,
  getSymbolFromCurrency,
  graphValue,
  formatTooltipHeaderUtilityBreakdownMonthly
} from '../../../../shared/Utils/Utils';

import './BreakdownChartsTheme.scss';
import {
  getConvertedValue,
  getSymbolFromFullName,
  UnitSymbols
} from '../../../../shared/Utils/unitHandling/unitHandling';
import { TooltipFormatterCallbackFunction } from 'highcharts';
import { renderToString } from 'react-dom/server';
import descriptors from '../../../../../locales/en-US/descriptors';
import { getUtilityTitle } from '../../UtilityBillViewDetailsUtils';
import {
  LINE_CHART,
  STACKED_BAR_CHART
} from '../../../../shared/Utils/Constants';
import { formatters } from '@hcecbp/i18n-utils';
import { ApiContext } from '../../../../../context/ApiContext';
import { UnitUtility } from '@bmsp/bmsp.web.component.common';

export interface IBreakdownChartsComponentProps {
  utilityType: string;
  currencyCode: string; // example USD, INR
  chartData: IBillBreakdownChartAggregations;
  isMonthly?: boolean;
}

const mapElectricUnits: { [key: string]: string } = {
  kVARH: 'kilovolt-ampere reactive hour',
  kWh: 'kilowatt hour',
  Wh: 'watt hour',
  kVA: 'kilovolt ampere',
  VA: 'volt ampere',
  W: 'watt',
  w: 'watt',
  kW: 'kilowatt',
  'kilowatt hour': 'kilowatt hour',
  'watt hour': 'watt hour',
  'kilovolt ampere': 'kilovolt ampere',
  'volt ampere': 'volt ampere',
  kilowatt: 'kilowatt',
  MWh: 'megawatt hour',
  GWh: 'gigawatt hour',
  TWh: 'terawatt hour',
  J: 'joule',
  kJ: 'kilo joules',
  KJ: 'kilojoule',
  MJ: 'megajoule',
  GJ: 'gigajoule',
  TJ: 'terajoule'
};

export const BreakdownChartsComponent = (
  props: IBreakdownChartsComponentProps
) => {
  const { consumptionOverview, demandOverview, costOverview } = props.chartData;
  const [t, lng] = useTranslation();
  const d = descriptors.energyManagement;
  const apiContext = useContext(ApiContext);
  const { userPreferedUnit } = apiContext;
  let fromUnit: string;
  // if (true) {
  //   fromUnit = userPreferedUnit as any;
  // } else {
  //   fromUnit = energyMeasurementUnit;
  // }
  fromUnit = 'kilowatt hour' as any;
  const demandUsageRateTooltipFormatter = (
    tooltipTitle: string,
    tooltipConfig: {
      numDecimals: number;
      pointFormatter?: (val: number) => string;
    }
  ): TooltipFormatterCallbackFunction => {
    const { numDecimals, pointFormatter } = tooltipConfig;

    return function(this: IAny, tooltip?: IAny) {
      const formattedTooltipDate = props.isMonthly
        ? formatTooltipHeaderUtilityBreakdownMonthly(this)
        : formatTooltipHeaderUtilityBreakdown(this);

      return renderToString(
        <div className="billbreakdown-demand-chart-tooltip-wrapper combined-tooltip-wrapper">
          <div className="tooltip-header">
            <span>{`${tooltipTitle} - ${formattedTooltipDate}`}</span>
          </div>
          <div>
            <table>
              {this.points.map((p: IAny, i: number) => {
                return (
                  <tr key={`${p.series.name}-${i}`}>
                    <td>
                      <span
                        className="highcharts-tooltip-label-marker"
                        style={{
                          color: p.color
                        }}
                      >
                        {p.series.type.toLowerCase() ===
                        LINE_CHART.toLowerCase()
                          ? '\u25AC'
                          : '\u25AE'}
                        {/* {'\u25CF'} */}
                      </span>
                    </td>
                    <td className="plot-label">{p.series.name}</td>
                    <td>
                      {pointFormatter
                        ? pointFormatter(p.y)
                        : p.y.toFixed(numDecimals || 1)}
                    </td>
                  </tr>
                );
              })}
            </table>
          </div>
        </div>
      );
    };
  };

  const conspTotalUsageMonthwise: IAny = [];

  const prepareChartData = () => {
    // Prepare Consumption Usage Stacked Column Chart Props
    const consumptionUsageArr = _.flattenDeep(
      consumptionOverview &&
        consumptionOverview.monthWiseData &&
        consumptionOverview.monthWiseData.length
        ? consumptionOverview.monthWiseData.map((m) =>
            m.subcategories.map((s) => s.usage)
          )
        : []
    );
    // usage unit should be cost
    // const consumptionUsageMaxValue = getMaxConvertedValueFromArray(
    //   props.utilityType,
    //   consumptionOverview.usageUnit,
    //   consumptionUsageArr
    // );

    const consumptionUsages = _.flatten(
      consumptionOverview &&
        consumptionOverview.monthWiseData &&
        consumptionOverview.monthWiseData.length
        ? consumptionOverview.monthWiseData.map((m) =>
            m.subcategories.map((cat) => cat.usage)
          )
        : []
    );

    const consumptionUsageMaxValue = getMaxConvertedValueFromArray(
      props.utilityType,
      fromUnit,
      consumptionUsages,
      mapElectricUnits[
        consumptionOverview && consumptionOverview.nativeRateUnit
          ? consumptionOverview.nativeRateUnit
          : 'kWh'
      ]
    ).Unit;

    // const consumptionUsageMaxValue = getSymbolFromFullName(
    //   mapElectricUnits[
    //     consumptionOverview && consumptionOverview.usageUnit
    //       ? consumptionOverview.usageUnit
    //       : consumptionOverview.nativeRateUnit
    //       ? consumptionOverview.nativeRateUnit
    //       : 'kWh'
    //   ],
    //   props.utilityType
    // );

    // const consumptionRateUnit: string = `${getSymbolFromCurrency(
    //   props.currencyCode
    // )}`;
    const consumptionRateUnit: string = getSymbolFromCurrency(
      props.currencyCode
    );

    const calculateNextMonthYear = (date: string) => {
      let month: number = Number(date.split('-')[1]);
      let year: number = Number(date.split('-')[0]);
      let day: number = Number(date.split('-')[2]);
      if (month === 12) {
        month = 1;
        year++;
      } else {
        month++;
      }
      // if (month === 12) {
      //   year++;
      // }
      return (
        year.toString() +
        '-' +
        String(month).padStart(2, '0') +
        '-' +
        String(day).padStart(2, '0')
      );
    };

    const checkIfMonthExists = (monthWiseData: IAny, monthEndDate: string) => {
      return monthWiseData.some((mnth: IAny) => {
        if (
          (Number(mnth.monthEndDate.split('-')[1]) ===
            Number(monthEndDate.split('-')[1]) &&
            Number(mnth.monthEndDate.split('-')[0]) ===
              Number(monthEndDate.split('-')[0])) ||
          (Number(mnth.monthStartDate.split('-')[1]) ===
            Number(monthEndDate.split('-')[1]) &&
            Number(mnth.monthStartDate.split('-')[0]) ===
              Number(monthEndDate.split('-')[0]))
        ) {
          return true;
        } else {
          return false;
        }
      });
    };

    const modifyPropsToAddMissingData = (
      overview: IAny,
      isDemand?: boolean
    ) => {
      const propsArr: string[] = [];
      const tempMonthWiseData = overview.monthWiseData;
      let ind = 0;
      const numOfItr = overview.monthWiseData.length;
      while (ind < numOfItr - 1) {
        const monthEndDate = calculateNextMonthYear(
          overview.monthWiseData[ind].monthEndDate
        );
        if (!checkIfMonthExists(tempMonthWiseData, monthEndDate)) {
          const mn = {
            monthStartDate: calculateNextMonthYear(
              overview.monthWiseData[ind].monthStartDate
            ),
            monthEndDate: calculateNextMonthYear(
              overview.monthWiseData[ind].monthEndDate
            ),
            subcategories: []
          };
          overview.monthWiseData.splice(ind + 1, 0, mn);
        }
        ind++;
      }

      if (isDemand) {
        overview.monthWiseData.forEach((ovr: IAny) => {
          if (!ovr.rate && !ovr.usage && !ovr.chargeAmount) {
            ovr.rate = 0;
            ovr.usage = 0;
            ovr.chargeAmount = 0;
          }
        });
      }
      overview.monthWiseData.forEach((ovr: IAny) => {
        ovr.subcategories &&
          ovr.subcategories.length &&
          ovr.subcategories.forEach((sub: IAny) => {
            if (!propsArr.includes(sub.actualName)) {
              propsArr.push(sub.actualName);
            }
          });
      });

      overview.monthWiseData.forEach((ovr: IAny) => {
        const tempArr = [...propsArr];
        ovr.subcategories &&
          ovr.subcategories.length &&
          ovr.subcategories.forEach((sub: IAny) => {
            if (propsArr.includes(sub.actualName)) {
              tempArr.splice(tempArr.indexOf(sub.actualName), 1);
            }
          });
        tempArr.forEach((ar: IAny) => {
          ovr.subcategories.push({ actualName: ar, name: ar });
        });
      });
    };
    consumptionOverview &&
      props.isMonthly &&
      modifyPropsToAddMissingData(consumptionOverview);

    const consumptionUsageMonthData = graphValue(
      consumptionOverview &&
        consumptionOverview.monthWiseData &&
        consumptionOverview.monthWiseData.length
        ? consumptionOverview.monthWiseData
        : [],
      lng
    );

    const demandUsageMOndData = graphValue(
      demandOverview &&
        demandOverview.monthWiseData &&
        demandOverview.monthWiseData.length
        ? demandOverview.monthWiseData
        : [],
      lng
    );

    costOverview &&
      props.isMonthly &&
      modifyPropsToAddMissingData(costOverview);
    const costUsageMonthData = graphValue(
      costOverview &&
        costOverview.monthWiseData &&
        costOverview.monthWiseData.length
        ? costOverview.monthWiseData
        : [],
      lng
    );

    const xaxisDataForConsumption = props.isMonthly
      ? _.zip(
          consumptionUsageMonthData.monthIndex,
          consumptionUsageMonthData.yearIndex
        ).map((a) => _.join(a, ', '))
      : consumptionUsageMonthData.xAxisIndex;

    consumptionOverview.monthWiseData.map((m, i) => {
      conspTotalUsageMonthwise.push({
        date: xaxisDataForConsumption[i],
        total: getConvertedValue(
          props.utilityType,
          mapElectricUnits[
            consumptionOverview.nativeRateUnit
              ? consumptionOverview.nativeRateUnit
              : 'kWh'
          ],
          m.totalUsage,
          consumptionUsageMaxValue
        ).Value,
        unit: consumptionUsageMaxValue
      });
    });

    const energyRateUnit = getSymbolFromCurrency(props.currencyCode);

    // consumptionOverview && modifyPropsToAddMissingData(consumptionOverview);
    const conspcurrencyCodeForLegend = getSymbolFromCurrency(
      props.currencyCode
    );
    const consumptionUsageChartProps: IStackedColumnLineCardProps = {
      titleString: t(d.BILLBREAKDOWN_OVERVIEW, {
        name: consumptionOverview && consumptionOverview.actualName,
        rate: consumptionUsageMaxValue + ' & ' + consumptionRateUnit
      }),
      isMonthly: props.isMonthly,
      chartProps: {
        isHighScaled: true,
        type: STACKED_BAR_CHART,
        colorList:
          consumptionOverview &&
          consumptionOverview.monthWiseData &&
          consumptionOverview.monthWiseData.length > 0
            ? consumptionOverview.monthWiseData[0].subcategories.map(
                (m, i) => chartColors(defaultColors)[i]
              )
            : [],
        chartData: {
          commonXAxis: props.isMonthly
            ? _.zip(
                consumptionUsageMonthData.monthIndex,
                consumptionUsageMonthData.yearIndex
              ).map((a) => _.join(a, ', '))
            : consumptionUsageMonthData.xAxisIndex,
          // legendOptions: {
          //   labelSuffix: ` (${consumptionRateUnit})`
          // },
          tooltipOptions: {
            formatter: consumptionUsageTooltipFormatter(
              props.currencyCode,
              t(d.COST, {
                name: consumptionOverview && consumptionOverview.actualName,
                rate: consumptionRateUnit
              }),
              {
                numDecimals: 2,
                dataUnit: consumptionRateUnit,
                usageUnit: consumptionUsageMaxValue,
                total: true,
                percentages: true,
                isMonthly: props.isMonthly
              },
              t,
              conspTotalUsageMonthwise
            )
          },
          colData: {
            colsYAxisProps: {
              opposite: false,
              labelSuffix: ` ${consumptionUsageMaxValue}`
            },
            data:
              consumptionOverview &&
              consumptionOverview.monthWiseData.map((m, i) => {
                return {
                  xlabel: `${consumptionUsageMonthData.monthIndex[i]} ${consumptionUsageMonthData.yearIndex[i]}`,
                  stackTotal: m.totalUsage,
                  stackData: m.subcategories.map((s, key) => ({
                    label:
                      t(s.actualName) + ' (' + consumptionUsageMaxValue + ')',
                    // color: chartColors(defaultColors)[key],
                    value:
                      getConvertedValue(
                        props.utilityType,
                        mapElectricUnits[
                          consumptionOverview.nativeRateUnit
                            ? consumptionOverview.nativeRateUnit
                            : 'kWh'
                        ],
                        !isNaN(s.usage) ? s.usage : null,
                        consumptionUsageMaxValue
                      ).Value !== null
                        ? getConvertedValue(
                            props.utilityType,
                            mapElectricUnits[
                              consumptionOverview.nativeRateUnit
                                ? consumptionOverview.nativeRateUnit
                                : 'kWh'
                            ],
                            !isNaN(s.usage) ? s.usage : null,
                            consumptionUsageMaxValue
                          ).Value
                        : 0
                  }))
                };
              })
          },
          lineData: {
            lines:
              consumptionOverview &&
              consumptionOverview.monthWiseData.map((m, i) => ({
                label: t(`${consumptionOverview.actualName} `),
                color: chartColors(defaultColors)[i],
                data: m.subcategories.map((cat, key) => ({
                  label: t(cat.actualName) + ' (' + energyRateUnit + ')',
                  value: cat.cost ? cat.cost : 0
                  // color: chartColors(defaultColors)[key],
                }))
              })),
            linesYaxisProps: {
              opposite: true,
              labelPrefix: ` ${consumptionRateUnit}`
            }
          }
        }
      }
    };

    // Prepare Consumption Rate Stacked Column Chart Props
    const consumptionRateMonthData = graphValue(
      consumptionOverview &&
        consumptionOverview.monthWiseData &&
        consumptionOverview.monthWiseData.length
        ? consumptionOverview.monthWiseData
        : [],
      lng
    );

    const consumptionRateDisplayUnit = `${getSymbolFromCurrency(
      props.currencyCode
    )}/${getSymbolFromFullName(
      mapElectricUnits[
        consumptionOverview && consumptionOverview.nativeRateUnit
          ? consumptionOverview.nativeRateUnit
          : 'kWh'
      ],
      props.utilityType
    )}`;
    const consumptionRateChartProps: IStackedColumnLineCardProps = {
      titleString: t(d.ELECTRICITYBLENDEDRATE, {
        name: consumptionOverview && consumptionOverview.actualName,
        rate: consumptionRateDisplayUnit
      }),
      isMonthly: props.isMonthly,
      chartProps: {
        isHighScaled: true,
        colorList: [...chartColors(defaultColors)],
        chartData: {
          commonXAxis: props.isMonthly
            ? _.zip(
                consumptionUsageMonthData.monthIndex,
                consumptionUsageMonthData.yearIndex
              ).map((a) => _.join(a, ', '))
            : consumptionRateMonthData.xAxisIndex,
          legendOptions: {
            labelSuffix: ` (${consumptionRateDisplayUnit})`
          },
          tooltipOptions: {
            formatter: stackedColumnLineTooltipFormatter(
              t(consumptionOverview && consumptionOverview.actualName),
              {
                numDecimals: 2,
                dataUnit: consumptionRateDisplayUnit,
                total: false,
                percentages: false,
                isMonthly: props.isMonthly
              },
              t
            )
          },
          lineData: {
            lines:
              consumptionOverview &&
              consumptionOverview.monthWiseData.map((m, i) => ({
                label: t(`${consumptionOverview.actualName}`),
                color: chartColors(defaultColors)[i],
                data: m.subcategories.map((cat, key) => ({
                  label: cat.actualName,
                  // value: cat.rate ? cat.rate : 0
                  value: cat.rate ? Number((cat.rate ?? 0).toFixed(2)) : 0
                  // color: chartColors(defaultColors)[key],
                }))
              })),
            linesYaxisProps: {
              opposite: false,
              labelSuffix: ` ${consumptionRateDisplayUnit}`
            }
          }
          // lineDataTotal: {
          //   lineData:
          //     consumptionOverview &&
          //     consumptionOverview.monthWiseData.map((m, i) =>
          //       m.totalRate ? m.totalRate : 0
          //     ),
          //   label: 'Total Rate'
          // }
        }
      }
    };

    // Prepare Demand Usage and Rate Column + Line Chart Props
    // Uses Stacked Column Line Chart
    demandOverview &&
      props.isMonthly &&
      modifyPropsToAddMissingData(demandOverview, true);

    const demandUsageArr = _.flatten(
      demandOverview.monthWiseData.map((m) => m.usage)
    );
    const demandUsageMaxValue =
      demandOverview.usageUnit && demandOverview.usageUnit !== null
        ? getMaxConvertedValueFromArray(
            props.utilityType,
            mapElectricUnits[demandOverview.usageUnit],
            demandUsageArr
          )
        : { Unit: '' };
    const demandMonthData = graphValue(
      demandOverview.monthWiseData &&
        demandOverview.monthWiseData.length &&
        demandOverview.monthWiseData.length > 0
        ? demandOverview.monthWiseData
        : [],
      lng
    );
    const demandRateUnit =
      demandOverview.usageUnit &&
      demandOverview.usageUnit !== null &&
      `${getSymbolFromCurrency(props.currencyCode)}/${getSymbolFromFullName(
        mapElectricUnits[
          demandOverview && demandOverview.nativeRateUnit
            ? demandOverview.nativeRateUnit
            : demandOverview.usageUnit
        ],
        props.utilityType
      )}`;
    const demandChartProps: IStackedColumnLineCardProps = {
      titleString: t(d.DEMAND_OVERVIEW, {
        name: demandOverview.actualName,
        rate:
          demandRateUnit &&
          demandRateUnit !== null &&
          `(${demandUsageMaxValue.Unit} &  ${demandRateUnit})`
      }),
      isMonthly: props.isMonthly,
      chartProps: {
        colorList: [...chartColors(defaultColors)],

        chartData: {
          commonXAxis: props.isMonthly
            ? _.zip(
                demandMonthData.monthIndex,
                demandMonthData.yearIndex
              ).map((a) => _.join(a, ', '))
            : demandMonthData.xAxisIndex,
          tooltipOptions: {
            formatter: demandUsageRateTooltipFormatter(
              t(demandOverview.actualName),
              {
                numDecimals: 2
              }
            )
          },
          colData: {
            data: demandOverview.monthWiseData.map((m, i) => ({
              xlabel: `${demandMonthData.monthIndex[i]} ${demandMonthData.yearIndex[i]}`,
              stackData: [
                {
                  label: `${t(d.DEMAND)} (${demandUsageMaxValue.Unit})`,
                  value: getConvertedValue(
                    props.utilityType,
                    mapElectricUnits[demandOverview.usageUnit],
                    m.usage,
                    demandUsageMaxValue.Unit
                  ).Value!
                }
              ]
            })),
            colsYAxisProps: {
              opposite: false,
              labelSuffix: ` ${demandUsageMaxValue.Unit}`
            }
          },
          lineData: {
            lines: [
              {
                label: `${t(demandOverview.actualName)} (${demandRateUnit})`,
                color: chartColors(defaultColors)[1],
                data: demandOverview.monthWiseData.map((m, i) => ({
                  label: `${t(demandOverview.actualName)} (${demandRateUnit})`,
                  // value: m.rate//
                  value: Number((m.rate ?? 0).toFixed(2))
                  // value: Math.round((m.rate ?? 0)*100/100)
                }))
              }
            ],
            linesYaxisProps: {
              opposite: true,
              labelSuffix: ` ${demandRateUnit}`
            }
          }
        }
      }
    };

    // Prepare Utility Bill Cost Stacked Column Chart Props

    const costMonthData = graphValue(costOverview.monthWiseData, lng);

    const costChartProps: IStackedColumnLineCardProps = {
      titleString: t(d.BILLBREAKDOWN_COST_OVERVIEW, {
        utilityName: t(getUtilityTitle(props.utilityType)),
        rate: getSymbolFromCurrency(props.currencyCode)
      }),
      isMonthly: props.isMonthly,
      chartProps: {
        colorList: [...chartColors(defaultColors)],
        chartData: {
          commonXAxis: props.isMonthly
            ? _.zip(
                costUsageMonthData.monthIndex,
                costUsageMonthData.yearIndex
              ).map((a) => _.join(a, ', '))
            : costMonthData.xAxisIndex,
          tooltipOptions: {
            // ${getSymbolFromCurrency(props.currencyCode)}  is removed to make all the tooltips consistent accross all the graphs.
            formatter: electricityCostLineTooltipFormatter(
              props.currencyCode,
              t(d.BILLBREAKDOWN_COST_TOOLTIP_HEADER, {
                rate: getSymbolFromCurrency(props.currencyCode)
              }),
              {
                numDecimals: 2,
                dataUnit: getSymbolFromCurrency(props.currencyCode),
                total: true,
                percentages: false,
                pointFormatter: (val) => `${val}`,
                isMonthly: props.isMonthly
              },
              t
            )
          },
          lineData: {
            lines: costOverview.monthWiseData.map((m, i) => ({
              label: getSymbolFromCurrency(props.currencyCode),
              color: chartColors(defaultColors)[i],
              data: m.subcategories.map((cat, key) => ({
                label: cat.actualName,
                value: cat.cost ? cat.cost : 0
                // color: chartColors(defaultColors)[key],
              }))
            })),
            linesYaxisProps: {
              opposite: false,
              labelSuffix: getSymbolFromCurrency(props.currencyCode)
            }
          },
          lineDataTotal: {
            lineData: costOverview.monthWiseData.map((m, i) =>
              m.totalCost ? m.totalCost : 0
            ),
            label: 'Total Cost'
          }
        }
      }
    };

    return {
      consumptionUsageChartProps,
      consumptionRateChartProps,
      demandChartProps,
      costChartProps
    };
  };

  const {
    consumptionUsageChartProps,
    consumptionRateChartProps,
    demandChartProps,
    costChartProps
  } = prepareChartData();

  // const [displayalert, setDisplayalert] = useState(true);
  // const closealert = () => {
  //   setDisplayalert(false);
  // };

  // useEffect(() => {
  //   setDisplayalert(true);
  // }, [props.isMonthly]);

  return demandChartProps.chartProps.chartData.lineData?.lines &&
    demandChartProps.chartProps.chartData.lineData?.lines.length &&
    demandChartProps.chartProps.chartData.lineData?.lines[0].data &&
    demandChartProps.chartProps.chartData.lineData?.lines[0].data.length &&
    demandChartProps.chartProps.chartData.lineData?.lines[0].data.length > 0 ? (
    <div className="Breakdown_Notification">
      <Grid className="breakdown-charts-wrapper">
        <Grid.Row>
          <Grid.Column className="breakdown-chart" width={6} sWidth={12}>
            <StackedColumnLineChartCard
              isCostChart={true}
              {...costChartProps}
              currencyCode={props.currencyCode}
            />
          </Grid.Column>
          <Grid.Column className="breakdown-chart" width={6} sWidth={12}>
            <StackedColumnLineChartCard {...demandChartProps} />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column className="breakdown-chart" width={6} sWidth={12}>
            <StackedColumnLineChartCard
              currencyCode={props.currencyCode}
              isConsumptionUsageChart={true}
              {...consumptionUsageChartProps}
            />
          </Grid.Column>
          <Grid.Column className="breakdown-chart" width={6} sWidth={12}>
            <StackedColumnLineChartCard {...consumptionRateChartProps} />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  ) : (
    <div className="Breakdown_Notification">
      <Grid className="breakdown-charts-wrapper">
        <Grid.Row>
          <Grid.Column className="breakdown-chart" width={4} sWidth={12}>
            <StackedColumnLineChartCard
              isCostChart={true}
              {...costChartProps}
              currencyCode={props.currencyCode}
            />
          </Grid.Column>
          <Grid.Column className="breakdown-chart" width={4} sWidth={12}>
            <StackedColumnLineChartCard
              currencyCode={props.currencyCode}
              isConsumptionUsageChart={true}
              {...consumptionUsageChartProps}
            />
          </Grid.Column>
          <Grid.Column className="breakdown-chart" width={4} sWidth={12}>
            <StackedColumnLineChartCard {...consumptionRateChartProps} />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

interface IChartType {
  isCostChart?: boolean;
}

export interface IStackedColumnLineCardProps extends IChartType {
  titleString: string;
  chartProps: IStackedColumnLineChartProps;
  isMonthly?: boolean;
  currencyCode?: string;
  isConsumptionUsageChart?: boolean;
}

export const StackedColumnLineChartCard = (
  props: IStackedColumnLineCardProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  return (
    <Card className={`stacked-col-line-chart-card-container kpi-card-common`}>
      <Card.Header className="chart-card-header">
        <div className="chart-title">{props.titleString}</div>
      </Card.Header>
      <Card.Content className="chart-card-content">
        {(props.chartProps.chartData.colData?.data &&
          props.chartProps.chartData.colData?.data.length &&
          props.chartProps.chartData.colData?.data.length > 0) ||
        (props.chartProps.chartData.lineData?.lines &&
          props.chartProps.chartData.lineData?.lines.length &&
          props.chartProps.chartData.lineData?.lines[0].data &&
          props.chartProps.chartData.lineData?.lines[0].data.length &&
          props.chartProps.chartData.lineData?.lines[0].data.length > 0) ? (
          <StackedColumnLineChart
            {...props.chartProps}
            isMonthly={props.isMonthly}
            isCostChart={props.isCostChart ? props.isCostChart : false}
            currencyCode={props.currencyCode ? props.currencyCode : 'USD'}
            isConsumptionUsageChart={props.isConsumptionUsageChart}
          />
        ) : (
          <div className="NoData-Utility-BreakDown">{t(d.NODATA)}</div>
        )}
      </Card.Content>
    </Card>
  );
};
